import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TDictObject } from '../../shared/IDirectoriesService';

import type { AlertRuleContextDirectory, ContextOption } from './components/AlertRuleContextSelectionPanel';
import type { AlertRulesService } from './services/AlertRulesService';

export function useContextOptions(alertRulesService: AlertRulesService): AlertRuleContextDirectory[] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        id: 'stage',
        title: 'alertsManagerWidget:contextPanel.fields.stage',
        options: toOptions(alertRulesService.workClassifierStageTypes ?? []),
        multi: true,
        fieldName: 'stageIds',
      },
      {
        id: 'wellboreType',
        title: 'alertsManagerWidget:contextPanel.fields.wellboreType',
        options: toOptions(alertRulesService.wellboreTypes ?? [], 'customName'),
        multi: true,
        fieldName: 'wellboreTypeIds',
      },
      {
        id: 'trajectory',
        title: 'alertsManagerWidget:contextPanel.fields.trajectoryType',
        options: toOptions(alertRulesService.trajectoryTypes ?? []),
        multi: true,
        fieldName: 'trajectoryTypeIds',
      },
      {
        id: 'section',
        title: 'alertsManagerWidget:contextPanel.fields.section',
        options: toOptions(alertRulesService.sectionTypes ?? []),
        multi: true,
        fieldName: 'sectionTypeIds',
      },
      {
        id: 'stratUnit',
        title: 'alertsManagerWidget:contextPanel.fields.stratUnit',
        options: toOptions(alertRulesService.stratUnitTypes ?? []),
        multi: true,
        fieldName: 'stratUnitTypeIds',
      },
      {
        id: 'reservoir',
        title: 'alertsManagerWidget:contextPanel.fields.reservoir',
        options: toOptions(alertRulesService.reservoirTypes ?? []),
        multi: true,
        fieldName: 'reservoirIds',
      },
      {
        id: 'run',
        title: 'alertsManagerWidget:contextPanel.fields.run',
        options: toOptions(alertRulesService.runTypes ?? []),
        multi: true,
        fieldName: 'runTypeIds',
      },
      {
        id: 'holeTypes',
        title: 'alertsManagerWidget:contextPanel.fields.holeType',
        options: [
          { value: 'cased', label: t('alertsManagerWidget:autoAnomalies.holeType.cased') },
          { value: 'open', label: t('alertsManagerWidget:autoAnomalies.holeType.open') },
        ],
        multi: true,
        fieldName: 'holeTypes',
        valueType: 'string',
      },
      ...((alertRulesService.index === 'depth'
        ? []
        : [
            {
              id: 'operationType',
              title: 'alertsManagerWidget:contextPanel.fields.operation',
              options: toOptions(alertRulesService.autosupervisorOperations ?? []),
              multi: true,
              fieldName: 'operationTypeIds',
            },
          ]) satisfies AlertRuleContextDirectory[]),
    ],
    [
      alertRulesService.workClassifierStageTypes,
      alertRulesService.wellboreTypes,
      alertRulesService.trajectoryTypes,
      alertRulesService.sectionTypes,
      alertRulesService.stratUnitTypes,
      alertRulesService.reservoirTypes,
      alertRulesService.runTypes,
      alertRulesService.index,
      alertRulesService.autosupervisorOperations,
      t,
    ],
  );
}

function toOptions(list: TDictObject[], nameKey = 'name'): ContextOption[] {
  return list.map((obj) => ({
    label: (obj.data[nameKey]?.toString() ?? '').trim() || String(obj.id),
    value: typeof obj.id === 'number' ? obj.id : Number(obj.id) || 0,
  }));
}
